/**
 * sl-67kc.4.1: Automation-Install post-import DB cleanup.
 *
 * Frontend owns the DB doc lifecycle per DIP (Y.js/BlockSuite is frontend-
 * sovereign). After `ZipTransformer.importDocs` copies every doc from the
 * template ZIP — including input/output database docs discovered via
 * `template-packaging.service.ts:collectAutomationDeps` — this helper
 * consumes the `AutomationInstallOptions` captured by
 * `AutomationInstallDialog` and deletes the DB docs the user opted out
 * of.
 *
 * The automation block's `prop:inputTables[].docId`,
 * `prop:outputTables[].docId`, and `prop:sourceDocId` keep pointing at
 * the (now-deleted) docIds. That is intentional: the user is expected to
 * rebind their own DBs in the Automation Builder once the block lives in
 * their workspace. Silently remapping to some fallback DB would violate
 * the zero-fallbacks rule.
 */
import type { Store } from '@blocksuite/affine/store';

/** Dialog options for a Level-3 (automation) template install. */
export interface AutomationInstallOptions {
  /** Target doc id the automation block should live in, or `null` for a new doc. */
  targetDocId: string | null;
  /** Keep input-database docs imported from the ZIP. */
  createInputDbs: boolean;
  /** Keep output-database docs imported from the ZIP. */
  createOutputDbs: boolean;
}

interface AutomationBlockRefs {
  readonly sourceDocId: string | null;
  readonly inputDocIds: readonly string[];
  readonly outputDocIds: readonly string[];
}

/**
 * Walk every imported Store and collect the DB docIds referenced by the
 * automation blocks. The shape matches
 * `template-packaging.service.ts:collectAutomationDeps` so publish-time
 * discovery and install-time cleanup agree on the set of DB docs.
 */
export function collectAutomationDbDocIds(
  stores: readonly Store[]
): AutomationBlockRefs[] {
  const refs: AutomationBlockRefs[] = [];
  for (const store of stores) {
    const automationBlocks = store.getBlocksByFlavour('affine:automation');
    for (const block of automationBlocks) {
      const props = block.model.props as Record<string, unknown>;
      refs.push({
        sourceDocId: readString(props.sourceDocId),
        inputDocIds: readTableDocIds(props.inputTables),
        outputDocIds: readTableDocIds(props.outputTables),
      });
    }
  }
  return refs;
}

/**
 * Remove imported input/output DB docs according to
 * `AutomationInstallOptions`. Deletion is idempotent — silently no-ops
 * for docIds already removed. Returns the set of docIds deleted so the
 * caller can trim `installedDocIds` before calling `completeInstall`.
 */
export function applyAutomationInstallOptions(
  stores: readonly Store[],
  options: AutomationInstallOptions,
  removeDoc: (docId: string) => void
): readonly string[] {
  if (options.createInputDbs && options.createOutputDbs) {
    return [];
  }

  const refs = collectAutomationDbDocIds(stores);
  const databaseSystemDocIdsByPageDoc =
    collectDatabaseSystemDocIdsByPageDoc(stores);
  const removed = new Set<string>();

  for (const ref of refs) {
    if (!options.createInputDbs) {
      if (ref.sourceDocId) removed.add(ref.sourceDocId);
      for (const id of ref.inputDocIds) removed.add(id);
    }
    if (!options.createOutputDbs) {
      for (const id of ref.outputDocIds) removed.add(id);
    }
  }

  for (const docId of Array.from(removed)) {
    const systemDocIds = databaseSystemDocIdsByPageDoc.get(docId);
    if (!systemDocIds) continue;
    for (const systemDocId of systemDocIds) {
      removed.add(systemDocId);
    }
  }

  for (const docId of removed) {
    removeDoc(docId);
  }

  return Array.from(removed);
}

export function collectDatabaseSystemDocIdsByPageDoc(
  stores: readonly Store[]
): Map<string, Set<string>> {
  const systemDocIdsByPageDoc = new Map<string, Set<string>>();
  const storesById = new Map(stores.map(store => [store.id, store]));

  for (const store of stores) {
    const pageDocId = readString(store.id);
    if (!pageDocId) continue;

    const databaseBlocks = store.getBlocksByFlavour('affine:database');
    for (const block of databaseBlocks) {
      const props = block.model.props as Record<string, unknown>;
      const rootDocId = readString(props.databaseRootDocId);
      if (!rootDocId) continue;

      const current = systemDocIdsByPageDoc.get(pageDocId) ?? new Set<string>();
      current.add(rootDocId);
      const rootStore = storesById.get(rootDocId);
      if (rootStore) {
        for (const shardDocId of collectDatabaseShardDocIds(rootStore)) {
          current.add(shardDocId);
        }
      }
      systemDocIdsByPageDoc.set(pageDocId, current);
    }
  }

  return systemDocIdsByPageDoc;
}

export function collectDatabaseShardDocIds(
  rootStore: Store
): readonly string[] {
  const shardDocIds = new Set<string>();
  const databaseBlocks = rootStore.getBlocksByFlavour('affine:database');
  for (const block of databaseBlocks) {
    const props = block.model.props as Record<string, unknown>;
    const manifest = props.databaseShardManifest;
    if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) {
      continue;
    }
    const shards = (manifest as { shards?: unknown }).shards;
    if (!Array.isArray(shards)) continue;
    for (const shard of shards) {
      if (!shard || typeof shard !== 'object' || Array.isArray(shard)) {
        continue;
      }
      const record = shard as { serverDocId?: unknown };
      const serverDocId = readString(record.serverDocId);
      if (!serverDocId) {
        continue;
      }
      shardDocIds.add(serverDocId);
    }
  }
  return Array.from(shardDocIds);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readTableDocIds(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const docId = (entry as { docId?: unknown }).docId;
    const parsed = readString(docId);
    if (parsed) ids.push(parsed);
  }
  return ids;
}
